





































































































































































/* General */
import Vue from 'vue'
import MailboxVerificationLayout from '@/layouts/mailbox-verification-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import EmailClientFigure from '@/components/global/email-client-figure.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { IMailboxesOutputOutgoingEmailSettings } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import { IMailboxesInputOutgoingFromAddress } from '@/api/types/request.types'

export default Vue.extend({
  metaInfo: {
    title: 'Outgoing Email Setup',
  },

  components: {
    MailboxVerificationLayout,
    EntitySettingsLoading,
    EmailClientFigure,
  },

  data() {
    return {
      RouteAssistant,
      ColorAssistant,
      mailboxName: '',
      isLoading: true,
      isSubmitting: false,
      emailIsPublicDomain: false,
      pollingForOutgoingFromEmailVerification: false,
      pollingFailed: false,
      poller: null,
      pollCount: 0,
      pollIntervalInMS: 5000,
      pollMax: 36,
      forwardingFromAddress: '',
      outgoingEmailSettings: {} as IMailboxesOutputOutgoingEmailSettings,
      outgoingFromAddressData: {
        fromName: '',
        fromEmailAddress: '',
      } as IMailboxesInputOutgoingFromAddress,
      postmarkSenderSignatureConfirmationUrl: '',
      postmarkUrlClicked: false,

      stepPercentage: 100,
    }
  },

  computed: {
    accountID(): number {
      return Number(this.$route.params.accountId)
    },
    mailboxID(): number {
      return Number(this.$route.params.id)
    },
    hasCustomFromAddress(): boolean {
      return this.outgoingEmailSettings.dnsInfo !== null
    },
    disableSubmit(): boolean {
      return this.outgoingFromAddressData.fromName.trim() === ''
    },
    isCustomFromAddressSameAsForwardingAddress(): boolean {
      return (
        this.hasCustomFromAddress &&
        this.outgoingEmailSettings.outgoingFromEmail &&
        this.outgoingEmailSettings.outgoingFromEmail.emailAddress ===
          this.forwardingFromAddress
      )
    },
    hasPostmarkSenderSignatureConfirmationUrl(): boolean {
      return this.postmarkSenderSignatureConfirmationUrl !== ''
    },
  },

  watch: {
    pollCount: {
      handler(newValue) {
        if (newValue >= this.pollMax) {
          this.endPolling()
          this.pollingFailed = true
        }
      },
    },
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([
        this.getOutgoingEmailSettings(),
        this.getForwardingFromAddress(),
      ])
      this.mailboxName = this.outgoingEmailSettings.name
      this.outgoingFromAddressData.fromName = this.outgoingEmailSettings.outgoingFromEmail.name
      this.outgoingFromAddressData.fromEmailAddress = this.forwardingFromAddress
    } catch (error) {
      this.$error.emit(error, true)
    } finally {
      this.isLoading = false
    }
  },

  beforeDestroy() {
    this.endPolling()
  },

  methods: {
    goToMailbox() {
      RouteAssistant.goToMailbox(
        this.$router,
        this.accountID,
        this.mailboxID,
        this.mailboxName,
      )
    },
    goToMailboxAssignPeople() {
      RouteAssistant.goToMailboxSettingsAssignPeopleRoute(
        this.$router,
        this.accountID,
        this.mailboxID,
      )
    },
    goToMailboxOutgoingEmail() {
      RouteAssistant.goToMailboxSettingsOutgoingEmailRoute(
        this.$router,
        this.accountID,
        this.mailboxID,
      )
    },
    async getOutgoingEmailSettings() {
      this.outgoingEmailSettings = await MailboxesAPI.get_OutgoingEmailSettings(
        this.mailboxID,
      )
    },
    async getForwardingFromAddress() {
      try {
        const response = await MailboxesAPI.get_AutoForwarding(this.mailboxID)

        // Assumption: Newly added forwarding address is placed at the end on the backend...
        this.forwardingFromAddress =
          response.listForwardingEmailAddresses[
            response.listForwardingEmailAddresses.length - 1
          ]

        if (!this.forwardingFromAddress) {
          RouteAssistant.goToMailboxVerificationIntroRoute(
            this.$router,
            this.accountID,
            this.mailboxID,
          )
        }
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async setOutgoingEmailAndAwaitVerification() {
      try {
        this.isSubmitting = true
        const response = await MailboxesAPI.post_OutgoingFromAddress(
          this.mailboxID,
          this.outgoingFromAddressData,
        )
        this.pollingForOutgoingFromEmailVerification = true
        this.pollForOutgoingFromEmailVerification()
      } catch (error) {
        if (error.data.message.includes('public domain')) {
          this.emailIsPublicDomain = true
        } else {
          this.$error.emit(error, true)
        }
      } finally {
        this.isSubmitting = false
      }
    },
    clearPostmarkUrlInfo() {
      this.postmarkSenderSignatureConfirmationUrl = ''
      this.postmarkUrlClicked = false
    },
    endPolling() {
      clearInterval(this.poller)
      this.pollingForOutgoingFromEmailVerification = false
    },
    async pollForOutgoingFromEmailVerification() {
      // Clear just in case there's any rif-raff...
      this.endPolling()
      this.clearPostmarkUrlInfo()
      this.pollCount = 0
      this.poller = setInterval(async () => {
        this.pollCount++
        await this.confirmOutgoingFromEmailVerification()
      }, this.pollIntervalInMS)
      this.pollingForOutgoingFromEmailVerification = true
    },
    async confirmOutgoingFromEmailVerification() {
      try {
        // See if there's a Postmark link to click (which would then let the
        // following confirmation call succeed)
        if (!this.hasPostmarkSenderSignatureConfirmationUrl) {
          await this.retrievePostmarkSenderSignatureConfirmationUrl()
        }
        // If still pending, will throw error...
        await MailboxesAPI.post_ConfirmPendingOutgoingFromAddress(
          this.mailboxID,
        )
        // If passes, can assume email was confirmed...
        const response = await MailboxesAPI.get_OutgoingEmailSettings(
          this.mailboxID,
        )
        if (
          response.outgoingFromEmail.emailAddress === this.forwardingFromAddress
        ) {
          this.outgoingEmailSettings = response
          this.endPolling()
        }
      } catch (error) {
        // Don't emit anything...this is a polling method
      }
    },
    async retrievePostmarkSenderSignatureConfirmationUrl() {
      const response = await MailboxesAPI.get_PostmarkSenderSignatureConfirmationUrl(
        this.mailboxID,
      )

      if (response.url && response.url.length > 0) {
        this.postmarkSenderSignatureConfirmationUrl = response.url
      }
    },
  },
})
